/**
 * The web layer's half of StoreKit.
 *
 * Same shape as the Play sibling, and the ordering is again the whole design:
 *
 *   config from OUR server  ->  StoreKit sheet  ->  transaction id to OUR server  ->  Apple verifies
 *   ->  entitlement granted by the SAME code the web webhook uses  ->  only then, finish
 *
 * Nothing here decides an entitlement. StoreKit verifies transactions on the device and it is
 * tempting to treat that as proof, but it runs on hardware Nuvra does not control. The device
 * reports what it thinks happened; the server asks Apple what actually happened.
 *
 * Every function is safe to call on the web and on Android: the plugin is absent there,
 * `appStoreBillingAvailable()` is false, and nothing else runs.
 */

import { Capacitor, registerPlugin } from "@capacitor/core";
import { authHeader } from "@/api/authToken";

interface AppStoreProductInfo {
  productId: string;
  title?: string;
  description?: string;
  /** Apple's own formatting, in the customer's locale and currency. */
  price?: string;
}

interface StoreKitTransaction {
  transactionId: string;
  productId: string;
}

interface NuvraAppStoreBillingPlugin {
  isAvailable(): Promise<{ available: boolean; reason?: string }>;
  getProducts(o: { productIds: string[] }): Promise<{ products: AppStoreProductInfo[] }>;
  purchase(o: { productId: string; appAccountToken?: string }):
    Promise<{ cancelled: boolean; pending?: boolean; transactionId?: string; productId?: string }>;
  currentEntitlements(): Promise<{ transactions: StoreKitTransaction[] }>;
  finish(o: { transactionId: string }): Promise<{ finished: boolean }>;
}

const AppStoreBilling = registerPlugin<NuvraAppStoreBillingPlugin>("NuvraAppStoreBilling");

export interface AppStoreConfig {
  verificationConfigured: boolean;
  appAccountToken: string;
  subscriptions: Array<{ productId: string; tier: "starter" | "pro" | "studio" }>;
  credits: Array<{ productId: string; acuAmount: number }>;
}

export interface AppStoreGrantResult {
  status: "granted" | "not_entitled" | "refused";
  kind?: "subscription" | "credits";
  tier?: string;
  acuAmount?: number;
  finishRequired?: boolean;
  reason?: string;
}

/** True only where the native plugin is really present. */
export function appStoreBillingAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("NuvraAppStoreBilling");
}

async function api<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let message = "App Store purchases are unavailable right now.";
    try { const b = await res.json(); if (b?.error) message = String(b.error); } catch { /* keep default */ }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Product ids and the account binding, from OUR server. Never hardcoded in the app bundle. */
export function fetchAppStoreConfig(): Promise<AppStoreConfig> {
  return api<AppStoreConfig>("/api/billing/appstore/config");
}

/** Prices as Apple formats them, in the customer's own currency. Never our own USD strings. */
export async function loadAppStoreProducts(productIds: string[]): Promise<AppStoreProductInfo[]> {
  if (!appStoreBillingAvailable() || productIds.length === 0) return [];
  const { products } = await AppStoreBilling.getProducts({ productIds });
  return products;
}

async function verify(transactionId: string): Promise<AppStoreGrantResult> {
  const result = await api<AppStoreGrantResult>("/api/billing/appstore/verify", { transactionId });
  // Finishing LAST, and only on the server's say-so. A finished transaction leaves StoreKit's queue
  // permanently, so doing it earlier would discard the only proof the purchase happened and leave a
  // customer whose grant then failed with nothing to retry against.
  if (result.status === "granted" && result.finishRequired) {
    await AppStoreBilling.finish({ transactionId }).catch(() => {
      // Already granted. An unfinished transaction is simply retried by the next launch's sync.
    });
  }
  return result;
}

/**
 * Buy a plan or a credit pack.
 *
 * The returned status is the SERVER's, not the device's, so a caller cannot mistake "StoreKit said
 * success" for "the plan is active".
 */
export async function purchaseWithAppStore(params: {
  productId: string;
  config: AppStoreConfig;
}): Promise<AppStoreGrantResult | { status: "cancelled" } | { status: "pending" }> {
  if (!appStoreBillingAvailable()) throw new Error("App Store purchases are not available here.");
  if (!params.config.verificationConfigured) {
    // Refusing to sell is the right call. Taking the money and being unable to verify it leaves a
    // customer charged and unentitled, which is worse than an unavailable purchase button.
    throw new Error("Purchases are temporarily unavailable. Please try again shortly.");
  }

  const outcome = await AppStoreBilling.purchase({
    productId: params.productId,
    appAccountToken: params.config.appAccountToken,
  });
  if (outcome.cancelled) return { status: "cancelled" };
  // Ask to Buy, or a payment awaiting approval. Nothing has been charged, and the transaction
  // arrives later; the launch sync picks it up once it does.
  if (outcome.pending || !outcome.transactionId) return { status: "pending" };

  return verify(outcome.transactionId);
}

/**
 * Re-send everything StoreKit still considers current.
 *
 * The recovery path for a purchase that completed on the device but never reached the server, and
 * also how an Ask to Buy approval eventually lands. Failures are swallowed on purpose: this runs in
 * the background at launch and must never interrupt it.
 */
export async function syncAppStorePurchases(): Promise<void> {
  if (!appStoreBillingAvailable()) return;
  try {
    const { transactions } = await AppStoreBilling.currentEntitlements();
    if (transactions.length === 0) return;

    const { results } = await api<{ results: Array<AppStoreGrantResult & { transactionId: string }> }>(
      "/api/billing/appstore/sync",
      { transactionIds: transactions.map(t => t.transactionId) },
    );

    for (const r of results) {
      if (r.status !== "granted" || !r.finishRequired) continue;
      await AppStoreBilling.finish({ transactionId: r.transactionId }).catch(() => {});
    }
  } catch {
    // Offline, signed out, or the server is unreachable. The next launch tries again.
  }
}
